import React, { useState, useMemo, useEffect } from "react";
import {
  MapPin,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  RotateCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Check,
  AlertCircle,
  Settings2,
  CheckSquare,
  Square,
  Download,
  Loader2,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * DeliveryCoverageSummary Component
 * 
 * Graphical dashboard with unified /postcodes/find API support,
 * displaying 1 to 10 fixed distance cards with on-demand postcode loading
 * and direct multi-select configuration.
 */
export default function DeliveryCoverageSummary({
  coverageData,
  isLoading,
  onRefresh,
  onSelectRange,
  onConfigure,
  onLoadBandPostcodes,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [filterMode, setFilterMode] = useState("all"); // 'all' | 'gaps' | 'covered'

  // Per-band selected postcode groups: { [bandKey]: Array<string> }
  const [selectedGroupsByBand, setSelectedGroupsByBand] = useState({});

  // Per-band loaded data state: { [bandKey]: { groups, postcode_count, covered_count, isLoading, isLoaded, error } }
  const [loadedBandsData, setLoadedBandsData] = useState({});

  // Summary calculations from coverageData
  const {
    maxDistance = 8,
    coveredRanges = [],
    gaps = [],
  } = useMemo(() => {
    if (!coverageData) return { maxDistance: 8, coveredRanges: [], gaps: [] };
    return {
      maxDistance: Number(coverageData.max_distance) || 8,
      coveredRanges: coverageData.covered_ranges || [],
      gaps: coverageData.gaps || [],
    };
  }, [coverageData]);

  // Sync initial bands from coverageData if present
  useEffect(() => {
    if (coverageData?.bands && Array.isArray(coverageData.bands)) {
      const initialMap = {};
      coverageData.bands.forEach((b) => {
        if (b.groups && b.groups.length > 0) {
          const key = `${b.starting_distance}-${b.ending_distance}`;
          initialMap[key] = {
            groups: b.groups,
            postcode_count: Number(b.postcode_count) || 0,
            covered_count: Number(b.covered_count) || 0,
            isLoaded: true,
            isLoading: false,
          };
        }
      });
      if (Object.keys(initialMap).length > 0) {
        setLoadedBandsData((prev) => ({ ...prev, ...initialMap }));
      }
    }
  }, [coverageData]);

  // Reset selection whenever coverageData updates
  useEffect(() => {
    setSelectedGroupsByBand({});
  }, [coverageData]);

  // Generate 10 fixed distance cards (0-1, 1-2, 2-3, 3-4, 4-5, 5-6, 6-7, 7-8, 8-9, 9-10)
  const fixedBands = useMemo(() => {
    const count = Math.max(10, Number(maxDistance) || 10);
    const list = [];
    for (let i = 0; i < count; i++) {
      const start = i;
      const end = i + 1;
      list.push({
        starting_distance: start,
        ending_distance: end,
        key: `${start}-${end}`,
      });
    }
    return list;
  }, [maxDistance]);

  // Determine initial covered/gap status of a band from covered_ranges and gaps
  const getBandRangeStatus = (start, end) => {
    const key = `${start}-${end}`;
    const detailed = loadedBandsData[key];

    if (detailed && detailed.isLoaded) {
      if (detailed.covered_count > 0 && detailed.covered_count >= detailed.postcode_count) {
        return "covered";
      }
      if (detailed.covered_count > 0) {
        return "partial";
      }
      return "gap";
    }

    // Check summary covered_ranges
    const isCovered = coveredRanges.some(
      (r) => start >= Number(r.starting_distance) && end <= Number(r.ending_distance)
    );
    if (isCovered) return "covered";

    const isPartial = coveredRanges.some(
      (r) => start < Number(r.ending_distance) && end > Number(r.starting_distance)
    );
    if (isPartial) return "partial";

    return "gap";
  };

  // Filtered fixed bands according to user toggle
  const filteredFixedBands = useMemo(() => {
    if (filterMode === "gaps") {
      return fixedBands.filter((b) => getBandRangeStatus(b.starting_distance, b.ending_distance) === "gap");
    }
    if (filterMode === "covered") {
      return fixedBands.filter((b) => getBandRangeStatus(b.starting_distance, b.ending_distance) !== "gap");
    }
    return fixedBands;
  }, [fixedBands, filterMode, coveredRanges, gaps, loadedBandsData]);

  // Handle loading postcodes for a specific band on demand
  const handleLoadBand = async (band) => {
    const key = band.key || `${band.starting_distance}-${band.ending_distance}`;
    setLoadedBandsData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), isLoading: true, error: null },
    }));

    try {
      if (onLoadBandPostcodes) {
        const res = await onLoadBandPostcodes({
          starting_distance: band.starting_distance,
          ending_distance: band.ending_distance,
        });

        if (res && res.bands && res.bands.length > 0) {
          const matchedBand =
            res.bands.find(
              (b) =>
                Number(b.starting_distance) === Number(band.starting_distance) &&
                Number(b.ending_distance) === Number(band.ending_distance)
            ) || res.bands[0];

          setLoadedBandsData((prev) => ({
            ...prev,
            [key]: {
              groups: matchedBand.groups || [],
              postcode_count: Number(matchedBand.postcode_count) || 0,
              covered_count: Number(matchedBand.covered_count) || 0,
              isLoaded: true,
              isLoading: false,
            },
          }));
        } else {
          setLoadedBandsData((prev) => ({
            ...prev,
            [key]: {
              groups: [],
              postcode_count: 0,
              covered_count: 0,
              isLoaded: true,
              isLoading: false,
            },
          }));
        }
      }
    } catch (err) {
      setLoadedBandsData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          isLoading: false,
          error: "Failed to load postcodes",
        },
      }));
    }
  };

  // Toggle selection of a postcode group in a band
  const handleToggleGroup = (bandKey, prefix) => {
    setSelectedGroupsByBand((prev) => {
      const current = prev[bandKey] || [];
      const isSelected = current.includes(prefix);
      const next = isSelected
        ? current.filter((p) => p !== prefix)
        : [...current, prefix];
      return { ...prev, [bandKey]: next };
    });
  };

  // Select all groups in a band
  const handleSelectAllInBand = (bandKey, groups) => {
    setSelectedGroupsByBand((prev) => ({
      ...prev,
      [bandKey]: (groups || []).map((g) => g.postcode_first_part),
    }));
  };

  // Select only uncovered groups in a band
  const handleSelectUncoveredInBand = (bandKey, groups) => {
    const uncov = (groups || [])
      .filter((g) => !g.covered)
      .map((g) => g.postcode_first_part);
    setSelectedGroupsByBand((prev) => ({
      ...prev,
      [bandKey]: uncov,
    }));
  };

  // Clear group selection in a band
  const handleClearInBand = (bandKey) => {
    setSelectedGroupsByBand((prev) => ({
      ...prev,
      [bandKey]: [],
    }));
  };

  // Trigger configure action for a band
  const handleConfigureBand = (band, detailedData) => {
    const bandKey = band.key || `${band.starting_distance}-${band.ending_distance}`;
    const selected = selectedGroupsByBand[bandKey] || [];
    const allGroups = detailedData?.groups || [];

    const groupsToConfig =
      selected.length > 0
        ? allGroups.filter((g) => selected.includes(g.postcode_first_part))
        : allGroups;

    const postcodes = groupsToConfig.map((g) => g.postcode_first_part);
    const totalSelectedCount = groupsToConfig.reduce(
      (sum, g) => sum + (Number(g.postcode_count) || 0),
      0
    );
    const totalServerCount = Number(detailedData?.postcode_count) || totalSelectedCount;

    if (onConfigure) {
      onConfigure({
        starting_distance: band.starting_distance,
        ending_distance: band.ending_distance,
        postcodes,
        totalSelectedCount,
        totalServerCount,
      });
    } else if (onSelectRange) {
      onSelectRange({
        starting_distance: String(band.starting_distance),
        ending_distance: String(band.ending_distance),
        post_code_start_with: postcodes[0] || "",
      });
    }
  };

  if (isLoading && !coverageData) {
    return (
      <div className="rounded-xl border border-neutral-800 bg-cblack3/90 p-6 space-y-4 animate-pulse">
        <div className="flex items-center justify-between">
          <div className="h-6 w-48 bg-neutral-800 rounded"></div>
          <div className="h-8 w-24 bg-neutral-800 rounded"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-neutral-800/60 rounded-lg"></div>
          ))}
        </div>
        <div className="h-12 bg-neutral-800/40 rounded-lg"></div>
      </div>
    );
  }

  if (!coverageData && !isLoading) {
    return null;
  }

  return (
    <div className="rounded-xl border border-neutral-800/80 bg-gradient-to-b from-cblack3 to-cblack2 p-5 shadow-xl text-cwhite space-y-5 transition-all">
      {/* Top Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800/80 pb-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-cyellow/10 border border-cyellow/30 flex items-center justify-center text-cyellow shadow-inner">
            <ShieldCheck size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold tracking-wide text-cwhite">
                Delivery Coverage Dashboard
              </h2>
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-800 text-cwhite3 font-medium border border-neutral-700">
                Radius 0 - {maxDistance} mi
              </span>
            </div>
            <p className="text-xs text-cwhite3">
              1 to 10 distance bands with on-demand postcode loading and multi-select configuration
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {onRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isLoading}
              className="h-8 px-3 text-xs bg-cblack2 border-neutral-700 hover:bg-neutral-800 text-cwhite3 hover:text-white"
            >
              <RotateCw
                size={14}
                className={`mr-1.5 ${isLoading ? "animate-spin text-cyellow" : ""}`}
              />
              Refresh Summary
            </Button>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsCollapsed((prev) => !prev)}
            className="h-8 px-2 text-xs text-cwhite3 hover:text-white"
            title={isCollapsed ? "Expand dashboard" : "Collapse dashboard"}
          >
            {isCollapsed ? (
              <span className="flex items-center gap-1">
                Show Overview <ChevronDown size={14} />
              </span>
            ) : (
              <span className="flex items-center gap-1">
                Hide <ChevronUp size={14} />
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content (Collapsible) */}
      {!isCollapsed && (
        <>
          {/* KPI Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Card 1: Max Distance */}
            <div className="rounded-lg border border-neutral-800 bg-cblack4/70 p-3.5 flex flex-col justify-between hover:border-neutral-700 transition-colors">
              <div className="flex items-center justify-between text-xs text-cwhite3">
                <span>Max Delivery Radius</span>
                <MapPin size={16} className="text-cyan-400" />
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-cwhite">{maxDistance}</span>
                <span className="text-xs text-cyan-400 font-medium">Miles Max</span>
              </div>
              <div className="mt-1 text-[11px] text-cwhite3/70">
                Configured store delivery perimeter
              </div>
            </div>

            {/* Card 2: Coverage Health */}
            <div className="rounded-lg border border-neutral-800 bg-cblack4/70 p-3.5 flex flex-col justify-between hover:border-neutral-700 transition-colors">
              <div className="flex items-center justify-between text-xs text-cwhite3">
                <span>Coverage Status</span>
                <span className="text-xs font-semibold text-cgreen">
                  {coveredRanges.length} active
                </span>
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-2xl font-bold text-cwhite">
                  {coveredRanges.length}
                </span>
                <span className="text-xs text-cwhite3">
                  / {fixedBands.length} distance bands
                </span>
              </div>
              <div className="mt-2 w-full bg-neutral-800 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-cgreen h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, Math.max(0, Math.round((coveredRanges.length / fixedBands.length) * 100)))}%`,
                  }}
                />
              </div>
            </div>

            {/* Card 3: Configured Ranges */}
            <div className="rounded-lg border border-neutral-800 bg-cblack4/70 p-3.5 flex flex-col justify-between hover:border-neutral-700 transition-colors">
              <div className="flex items-center justify-between text-xs text-cwhite3">
                <span>Configured Ranges</span>
                <CheckCircle2 size={16} className="text-cgreen" />
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="text-2xl font-bold text-cgreen">
                  {coveredRanges.length}
                </span>
                <span className="text-xs text-cwhite3">Active Zone{coveredRanges.length === 1 ? '' : 's'}</span>
              </div>
              <div className="mt-1 text-[11px] text-cwhite3/80 truncate">
                {coveredRanges.length > 0
                  ? coveredRanges.map((r) => `${r.starting_distance}-${r.ending_distance}mi`).join(", ")
                  : "No ranges configured yet"}
              </div>
            </div>

            {/* Card 4: Gaps Alert */}
            <div
              className={`rounded-lg border p-3.5 flex flex-col justify-between transition-colors ${
                gaps.length > 0
                  ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/60"
                  : "border-neutral-800 bg-cblack4/70"
              }`}
            >
              <div className="flex items-center justify-between text-xs text-cwhite3">
                <span className="text-amber-400 font-medium">Unconfigured Gaps</span>
                <AlertTriangle size={16} className="text-amber-400" />
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className={`text-2xl font-bold ${gaps.length > 0 ? "text-amber-400" : "text-cgreen"}`}>
                  {gaps.length}
                </span>
                <span className="text-xs text-cwhite3">Missing Gap{gaps.length === 1 ? '' : 's'}</span>
              </div>
              <div className="mt-1 text-[11px] text-amber-300/80 truncate">
                {gaps.length > 0
                  ? gaps.map((g) => `${g.starting_distance}-${g.ending_distance}mi`).join(", ")
                  : "All distance bands covered!"}
              </div>
            </div>
          </div>

          {/* Interactive Distance Timeline / Track */}
          <div className="space-y-2 rounded-lg border border-neutral-800 bg-cblack4/50 p-3.5">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
              <span className="font-medium text-cwhite flex items-center gap-1.5">
                <Sparkles size={14} className="text-cyellow" />
                Interactive Distance Track (0 to {maxDistance} mi)
              </span>
              <span className="text-[11px] text-cwhite3">
                Click any segment to scroll & load postcodes
              </span>
            </div>

            {/* The Visual Segment Bar */}
            <div className="flex w-full h-9 rounded-md overflow-hidden bg-neutral-900 border border-neutral-800 p-0.5 gap-1">
              {fixedBands.slice(0, maxDistance || 10).map((band, idx) => {
                const status = getBandRangeStatus(band.starting_distance, band.ending_distance);
                const isFullyCovered = status === "covered";
                const isPartiallyCovered = status === "partial";
                const isGap = status === "gap";

                let bgClass = "bg-neutral-800 text-neutral-400 hover:bg-neutral-700";
                let borderClass = "border-transparent";
                if (isFullyCovered) {
                  bgClass = "bg-emerald-600/90 hover:bg-emerald-500 text-white font-medium shadow-sm";
                } else if (isPartiallyCovered) {
                  bgClass = "bg-amber-600/90 hover:bg-amber-500 text-white font-medium shadow-sm";
                } else if (isGap) {
                  bgClass = "bg-neutral-800/90 hover:bg-red-950/60 text-red-300 border border-dashed border-red-500/40";
                }

                return (
                  <button
                    key={`track-${band.key}-${idx}`}
                    type="button"
                    style={{ flex: 1 }}
                    onClick={() => {
                      handleLoadBand(band);
                    }}
                    className={`h-full rounded text-xs flex items-center justify-center gap-1 transition-all group relative px-1 cursor-pointer select-none ${bgClass} ${borderClass}`}
                    title={`Band: ${band.starting_distance}-${band.ending_distance} mi (${status}) - Click to load`}
                  >
                    <span className="truncate text-[11px]">
                      {band.starting_distance}-{band.ending_distance}mi
                    </span>
                    {isFullyCovered && <Check size={11} className="shrink-0" />}
                    {isPartiallyCovered && <AlertCircle size={11} className="shrink-0 text-amber-200" />}
                    {isGap && <span className="text-[10px] text-red-400 font-bold shrink-0">GAP</span>}
                  </button>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-4 text-[11px] text-cwhite3 pt-1">
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-emerald-500"></span>
                <span>Covered ({coveredRanges.length})</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-amber-500"></span>
                <span>Partial</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-neutral-800 border border-dashed border-red-500/70"></span>
                <span>Unconfigured Gap ({gaps.length})</span>
              </div>
            </div>
          </div>

          {/* 1 to 10 Fixed Distance Bands Grid */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-cyellow" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-cwhite3">
                  Fixed Distance Bands (1 to {fixedBands.length})
                </h3>
                <Badge variant="outline" className="text-[10px] h-5 border-neutral-700 text-cwhite3">
                  {filteredFixedBands.length} Bands
                </Badge>
              </div>

              {/* Filter chips */}
              <div className="flex items-center gap-1 bg-cblack4 rounded-md p-0.5 border border-neutral-800 text-xs">
                <button
                  type="button"
                  onClick={() => setFilterMode("all")}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                    filterMode === "all"
                      ? "bg-neutral-700 text-white shadow-sm"
                      : "text-cwhite3 hover:text-white"
                  }`}
                >
                  All ({fixedBands.length})
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("gaps")}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                    filterMode === "gaps"
                      ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                      : "text-cwhite3 hover:text-white"
                  }`}
                >
                  Gaps
                </button>
                <button
                  type="button"
                  onClick={() => setFilterMode("covered")}
                  className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${
                    filterMode === "covered"
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                      : "text-cwhite3 hover:text-white"
                  }`}
                >
                  Covered
                </button>
              </div>
            </div>

            {/* Bands Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
              {filteredFixedBands.map((band, bandIdx) => {
                const bandKey = band.key;
                const detailedData = loadedBandsData[bandKey];
                const isLoaded = detailedData && detailedData.isLoaded;
                const isCardLoading = detailedData && detailedData.isLoading;

                const status = getBandRangeStatus(band.starting_distance, band.ending_distance);
                const isFullyCovered = status === "covered";
                const isPartiallyCovered = status === "partial";
                const isGap = status === "gap";

                const selectedInThisBand = selectedGroupsByBand[bandKey] || [];
                const hasSelection = selectedInThisBand.length > 0;

                const allGroups = detailedData?.groups || [];
                const uncoveredGroups = allGroups.filter((g) => !g.covered);

                const bandPct =
                  detailedData && Number(detailedData.postcode_count) > 0
                    ? Math.round(
                        (Number(detailedData.covered_count) / Number(detailedData.postcode_count)) * 100
                      )
                    : 0;

                const selectedPostcodesCount = allGroups
                  .filter((g) => selectedInThisBand.includes(g.postcode_first_part))
                  .reduce((sum, g) => sum + (Number(g.postcode_count) || 0), 0);

                return (
                  <div
                    key={`band-card-${bandKey}-${bandIdx}`}
                    className={`rounded-lg border p-4 flex flex-col justify-between space-y-3.5 transition-all ${
                      hasSelection
                        ? "border-cyellow/60 bg-cblack3 shadow-lg ring-1 ring-cyellow/20"
                        : isFullyCovered
                        ? "border-emerald-500/30 bg-emerald-950/10 hover:border-emerald-500/50"
                        : isPartiallyCovered
                        ? "border-amber-500/30 bg-amber-950/10 hover:border-amber-500/50"
                        : "border-neutral-800 bg-cblack4/60 hover:border-neutral-700"
                    }`}
                  >
                    {/* Card Header */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-cwhite">
                          {band.starting_distance} - {band.ending_distance} Miles
                        </span>
                        {isFullyCovered && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-medium">
                            Covered
                          </span>
                        )}
                        {isPartiallyCovered && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 font-medium">
                            Partial {isLoaded ? `(${bandPct}%)` : ""}
                          </span>
                        )}
                        {isGap && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 font-medium">
                            Gap / Unconfigured
                          </span>
                        )}
                      </div>

                      {/* Right Action: Configure Button (if loaded) or Load Button */}
                      {isLoaded ? (
                        <div className="flex items-center gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleLoadBand(band)}
                            disabled={isCardLoading}
                            className="h-8 w-8 p-0 text-cwhite3 hover:text-white"
                            title="Reload postcodes for this band"
                          >
                            <RotateCw
                              size={13}
                              className={isCardLoading ? "animate-spin text-cyellow" : ""}
                            />
                          </Button>

                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleConfigureBand(band, detailedData)}
                            className={`h-8 text-xs px-3 font-semibold transition-all shadow-md flex items-center gap-1.5 ${
                              hasSelection
                                ? "bg-cyellow text-black hover:bg-cyellow/90 ring-2 ring-cyellow/40"
                                : "bg-cyellow/90 text-black hover:bg-cyellow"
                            }`}
                          >
                            <Settings2 size={13} />
                            {hasSelection
                              ? `Configure (${selectedInThisBand.length})`
                              : `Configure (${allGroups.length})`}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleLoadBand(band)}
                          disabled={isCardLoading}
                          className="h-8 text-xs px-3 bg-neutral-800 hover:bg-neutral-700 text-cwhite border border-neutral-700 hover:border-neutral-600 flex items-center gap-1.5 shadow-sm"
                        >
                          {isCardLoading ? (
                            <>
                              <Loader2 size={13} className="animate-spin text-cyellow" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Download size={13} className="text-cyellow" />
                              Load Postcodes
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Body: Loaded vs Unloaded State */}
                    {isCardLoading && (
                      <div className="py-4 flex items-center justify-center gap-2 text-xs text-cwhite3 animate-pulse bg-neutral-900/40 rounded-md border border-neutral-800">
                        <Loader2 size={16} className="animate-spin text-cyellow" />
                        <span>Fetching postcodes for {band.starting_distance} - {band.ending_distance} mi...</span>
                      </div>
                    )}

                    {!isCardLoading && !isLoaded && (
                      <div className="py-3 px-3.5 bg-neutral-900/30 rounded-md border border-neutral-800/70 flex items-center justify-between text-xs text-cwhite3">
                        <span>Click "Load Postcodes" to fetch groups in this range.</span>
                        <button
                          type="button"
                          onClick={() => handleLoadBand(band)}
                          className="text-xs text-cyellow hover:underline font-medium flex items-center gap-1"
                        >
                          Load now →
                        </button>
                      </div>
                    )}

                    {!isCardLoading && isLoaded && (
                      <>
                        {/* Progress Bar */}
                        <div className="space-y-1">
                          <div className="flex justify-between text-xs text-cwhite3">
                            <span>
                              {Number(detailedData.covered_count).toLocaleString()} /{" "}
                              {Number(detailedData.postcode_count).toLocaleString()} postcodes configured
                            </span>
                            <span className="font-semibold">{bandPct}%</span>
                          </div>
                          <div className="w-full bg-neutral-800 h-1.5 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                isFullyCovered
                                  ? "bg-emerald-500"
                                  : isPartiallyCovered
                                  ? "bg-amber-500"
                                  : "bg-neutral-600"
                              }`}
                              style={{ width: `${Math.min(100, Math.max(0, bandPct))}%` }}
                            />
                          </div>
                        </div>

                        {/* Multi-Select Toolbar & Counter */}
                        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-neutral-800/80 text-xs">
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <span className="text-cwhite3/80 font-medium">Select:</span>
                            <button
                              type="button"
                              onClick={() => handleSelectAllInBand(bandKey, allGroups)}
                              className="px-1.5 py-0.5 rounded bg-neutral-800 hover:bg-neutral-700 text-cwhite3 hover:text-white border border-neutral-700"
                              title="Select all groups in this band"
                            >
                              All ({allGroups.length})
                            </button>
                            {uncoveredGroups.length > 0 && (
                              <button
                                type="button"
                                onClick={() => handleSelectUncoveredInBand(bandKey, allGroups)}
                                className="px-1.5 py-0.5 rounded bg-neutral-800 hover:bg-neutral-700 text-amber-300 hover:text-amber-200 border border-neutral-700"
                                title="Select only uncovered groups"
                              >
                                Uncovered ({uncoveredGroups.length})
                              </button>
                            )}
                            {hasSelection && (
                              <button
                                type="button"
                                onClick={() => handleClearInBand(bandKey)}
                                className="px-1.5 py-0.5 rounded bg-neutral-800 hover:bg-neutral-700 text-red-300 hover:text-red-200 border border-neutral-700"
                                title="Clear group selection"
                              >
                                Clear
                              </button>
                            )}
                          </div>

                          {hasSelection && (
                            <div className="text-[11px] text-cyellow font-medium">
                              {selectedInThisBand.length} group{selectedInThisBand.length === 1 ? '' : 's'} ({selectedPostcodesCount.toLocaleString()} postcodes)
                            </div>
                          )}
                        </div>

                        {/* Group Badges List with Selection Toggle */}
                        <div className="space-y-1.5">
                          <div className="text-[11px] text-cwhite3/70 flex items-center justify-between">
                            <span>Click groups to select / deselect:</span>
                            <span className="text-[10px] text-cwhite3/50">
                              {allGroups.length} groups total
                            </span>
                          </div>

                          {allGroups.length === 0 ? (
                            <div className="text-xs text-cwhite3/60 py-2">
                              No postcode groups found in this range.
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1 select-none">
                              {allGroups.map((grp) => {
                                const isGrpCovered = grp.covered === true;
                                const isGrpPartial = grp.partially_covered === true;
                                const isSelected = selectedInThisBand.includes(grp.postcode_first_part);

                                let chipStyle = "";
                                if (isSelected) {
                                  chipStyle =
                                    "bg-cyellow/20 border-cyellow text-white font-semibold shadow-sm ring-1 ring-cyellow/50";
                                } else if (isGrpCovered) {
                                  chipStyle =
                                    "bg-emerald-950/40 border-emerald-600/40 text-emerald-300 hover:border-emerald-500";
                                } else if (isGrpPartial) {
                                  chipStyle =
                                    "bg-amber-950/40 border-amber-600/40 text-amber-300 hover:border-amber-500";
                                } else {
                                  chipStyle =
                                    "bg-neutral-800/80 border-neutral-700 text-neutral-300 hover:border-neutral-500";
                                }

                                return (
                                  <button
                                    key={grp.postcode_first_part}
                                    type="button"
                                    onClick={() => handleToggleGroup(bandKey, grp.postcode_first_part)}
                                    className={`text-[11px] px-2.5 py-1 rounded-md border flex items-center gap-1.5 transition-all cursor-pointer ${chipStyle}`}
                                    title={`${grp.postcode_first_part} (${grp.covered_count || 0}/${grp.postcode_count} covered) - Click to toggle selection`}
                                  >
                                    {/* Selection Indicator Checkbox */}
                                    {isSelected ? (
                                      <CheckSquare size={12} className="text-cyellow shrink-0" />
                                    ) : (
                                      <Square size={12} className="text-neutral-500 shrink-0 opacity-60" />
                                    )}

                                    <span className="font-semibold">{grp.postcode_first_part}</span>
                                    <span className="text-[10px] opacity-80">
                                      ({grp.postcode_count})
                                    </span>

                                    {isGrpCovered && !isSelected && (
                                      <Check size={11} className="text-emerald-400 shrink-0" />
                                    )}
                                    {isGrpPartial && !isSelected && (
                                      <AlertCircle size={11} className="text-amber-400 shrink-0" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
